import type {
    SignupStep,
    StepAuthentication,
    StepCompanyChoice,
    StepCompanyDetails,
    StepEmailAwaitingValidation,
    StepRedirectUser,
    StepUserDetails,
} from '@api';
import type {TranslationKey} from '#build/i18n';

export const PAGES = {
    EMAIL: '/email',
    PASSWORD: '/password',
    PUBLIC_EMAIL_WARNING: '/public-email-warning',
    EMAIL_VALIDATION: '/email-validation',
    COMPANY_CHOICE: '/company-choice',
    USER_DETAILS: '/user-details',
    PHONE_NUMBER: '/phone-number',
    COMPANY_DETAILS: '/company-details',
    USER_QUALIFICATION: '/user-qualification',
    WELCOME: '/welcome-to-malt',
    EMAIL_VERIFICATION: '/email-verification',
};

export enum FunnelPageName {
    COMPANY_CHOICE = 'company-choice',
    COMPANY_DETAILS = 'company-details',
    EMAIL = 'email',
    EMAIL_VALIDATION = 'email-validation',
    PASSWORD = 'password',
    PHONE_NUMBER = 'phone-number',
    PUBLIC_EMAIL_WARNING = 'public-email-warning',
    USER_DETAILS = 'user-details',
    USER_QUALIFICATION = 'user-qualification',
    WELCOME = 'welcome',
    EMAIL_VERIFICATION = 'email-verification',
}

export type FunnelPage = {
    name: FunnelPageName;
    path: string;
};

export enum FunnelStepName {
    COMPANY_DETAILS = 'company-details',
    EMAIL_VALIDATION = 'email-validation',
    USER_DETAILS = 'user-details',
    USER_QUALIFICATION = 'user-qualification',
}

export type FunnelStep = {
    name: FunnelStepName;
    // TODO fix this! Should not use reserved names!
    number: number;
    paths: string[];
    label: TranslationKey;
};

export const STEPS: FunnelStep[] = [
    {
        name: FunnelStepName.EMAIL_VALIDATION,
        number: 1,
        paths: [PAGES.EMAIL_VALIDATION],
        label: 'signup.client.stepper.label.EMAIL_VALIDATION',
    },
    {
        name: FunnelStepName.USER_DETAILS,
        number: 2,
        paths: [PAGES.COMPANY_CHOICE, PAGES.USER_DETAILS],
        label: 'signup.client.stepper.label.USER_DETAILS',
    },
    {
        name: FunnelStepName.COMPANY_DETAILS,
        number: 3,
        paths: [PAGES.COMPANY_DETAILS],
        label: 'signup.client.stepper.label.COMPANY_DETAILS',
    },

    {
        name: FunnelStepName.USER_QUALIFICATION,
        number: 4,
        paths: [PAGES.USER_QUALIFICATION],
        label: 'signup.client.stepper.label.USER_QUALIFICATION',
    },
];

export const STEPS_USER_DETAILS_INVERTED_WITH_COMPANY_CHOICE: FunnelStep[] = [
    {
        name: FunnelStepName.EMAIL_VALIDATION,
        number: 1,
        paths: [PAGES.EMAIL_VALIDATION],
        label: 'signup.client.stepper.label.EMAIL_VALIDATION',
    },
    {
        name: FunnelStepName.USER_DETAILS,
        number: 2,
        paths: [PAGES.USER_DETAILS, PAGES.COMPANY_CHOICE],
        label: 'signup.client.stepper.label.USER_DETAILS',
    },
    {
        name: FunnelStepName.COMPANY_DETAILS,
        number: 3,
        paths: [PAGES.COMPANY_DETAILS],
        label: 'signup.client.stepper.label.COMPANY_DETAILS',
    },
    {
        name: FunnelStepName.USER_QUALIFICATION,
        number: 4,
        paths: [PAGES.USER_QUALIFICATION],
        label: 'signup.client.stepper.label.USER_QUALIFICATION',
    },
];

export const funnelPages: FunnelPage[] = [
    {name: FunnelPageName.COMPANY_CHOICE, path: PAGES.COMPANY_CHOICE},
    {name: FunnelPageName.COMPANY_DETAILS, path: PAGES.COMPANY_DETAILS},
    {name: FunnelPageName.EMAIL, path: PAGES.EMAIL},
    {name: FunnelPageName.EMAIL_VALIDATION, path: PAGES.EMAIL_VALIDATION},
    {name: FunnelPageName.PASSWORD, path: PAGES.PASSWORD},
    {name: FunnelPageName.PHONE_NUMBER, path: PAGES.PHONE_NUMBER},
    {name: FunnelPageName.PUBLIC_EMAIL_WARNING, path: PAGES.PUBLIC_EMAIL_WARNING},
    {name: FunnelPageName.USER_DETAILS, path: PAGES.USER_DETAILS},
    {name: FunnelPageName.USER_QUALIFICATION, path: PAGES.USER_QUALIFICATION},
    {name: FunnelPageName.WELCOME, path: PAGES.WELCOME},
    {name: FunnelPageName.EMAIL_VERIFICATION, path: PAGES.EMAIL_VERIFICATION},
];

export const allPagePaths = Object.values(PAGES);

export interface StepsToPagesMapper {
    StepAuthentication: typeof PAGES.EMAIL;
    StepPublicEmailWarning: typeof PAGES.PUBLIC_EMAIL_WARNING;
    StepEmailAwaitingValidation: typeof PAGES.EMAIL_VALIDATION;
    StepCompanyChoice: typeof PAGES.COMPANY_CHOICE;
    StepUserDetails: typeof PAGES.USER_DETAILS;
    StepPhoneNumber: typeof PAGES.PHONE_NUMBER;
    StepUserQualification: typeof PAGES.USER_QUALIFICATION;
    StepCompanyDetails: typeof PAGES.COMPANY_DETAILS;
}

export const stepsToPagesMapper: StepsToPagesMapper = {
    StepAuthentication: PAGES.EMAIL,
    StepPublicEmailWarning: PAGES.PUBLIC_EMAIL_WARNING,
    StepEmailAwaitingValidation: PAGES.EMAIL_VALIDATION,
    StepCompanyChoice: PAGES.COMPANY_CHOICE,
    StepUserDetails: PAGES.USER_DETAILS,
    StepPhoneNumber: PAGES.PHONE_NUMBER,
    StepUserQualification: PAGES.USER_QUALIFICATION,
    StepCompanyDetails: PAGES.COMPANY_DETAILS,
};

export function isStepAuthentication(step?: SignupStep): step is StepAuthentication {
    return step?.name === 'StepAuthentication';
}

export function isStepCompanyChoice(step?: SignupStep): step is StepCompanyChoice {
    return step?.name === 'StepCompanyChoice';
}

export function isStepCompanyDetails(step?: SignupStep): step is StepCompanyDetails {
    return step?.name === 'StepCompanyDetails';
}

export function isStepEmailAwaitingValidation(step?: SignupStep): step is StepEmailAwaitingValidation {
    return step?.name === 'StepEmailAwaitingValidation';
}

export function isStepRedirectUser(step?: SignupStep): step is StepRedirectUser {
    return step?.name === 'StepRedirectUser';
}

export function isStepUserDetails(step?: SignupStep): step is StepUserDetails {
    return step?.name === 'StepUserDetails';
}
